import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PaperPlaneTilt, XCircle, LinkSimple, EnvelopeSimple, Clock } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/helpers";

const STATUS_STYLES = {
  sent: "bg-blue-100 text-blue-800",
  opened: "bg-violet-100 text-violet-800",
  submitted: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-100 text-slate-600",
  superseded: "bg-slate-100 text-slate-500",
};

export default function IntakeManagementDialog({ open, onOpenChange, branches, defaultBranchId, onSubmitted }) {
  const [form, setForm] = useState({
    guardian_email: "", guardian_phone: "", student_name_hint: "",
    branch_id: "", note: "",
  });
  const [intakes, setIntakes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/intakes");
      setIntakes(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (open) {
      setForm(f => ({ ...f, branch_id: defaultBranchId || (branches[0]?.id || "") }));
      load();
    }
  }, [open, defaultBranchId, branches]);

  const create = async () => {
    setCreating(true);
    try {
      const payload = {
        guardian_email: form.guardian_email.trim() || null,
        guardian_phone: form.guardian_phone.trim() || null,
        student_name_hint: form.student_name_hint.trim() || null,
        branch_id: form.branch_id || null,
        note: form.note.trim() || null,
      };
      const { data } = await api.post("/intakes", payload);
      // reset non-branch fields
      setForm(f => ({ ...f, guardian_email: "", guardian_phone: "", student_name_hint: "", note: "" }));
      // copy the share URL for convenience — best effort (needs a secure context)
      let copied = false;
      try { await navigator.clipboard.writeText(data.share_url); copied = true; } catch { /* ignore */ }
      toast.success(copied ? "Intake link created and copied to clipboard" : "Intake link created");
      await load();
      onSubmitted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { toast.error("Copy failed — long-press to select the URL"); }
  };

  const resend = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/intakes/${id}/resend`);
      toast.success("Email resent");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Withdraw this intake link? The guardian won't be able to submit it.")) return;
    setBusyId(id);
    try {
      await api.post(`/intakes/${id}/cancel`);
      toast.success("Intake cancelled");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="intake-mgmt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Digital intake forms</DialogTitle>
          <p className="text-xs text-muted-foreground pt-1">
            Send a branded form to a new family. On submission, a <span className="font-medium text-foreground">Pending</span> student is auto-created — mark them Registered when onboarding is complete.
          </p>
        </DialogHeader>

        {/* Create new intake */}
        <div className="rounded-lg border border-dashed border-border p-4 space-y-3 bg-muted/30">
          <div className="text-sm font-semibold">Send a new intake link</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Guardian email <span className="text-muted-foreground text-[11px]">(we&rsquo;ll email the link)</span></Label>
              <Input
                type="email" placeholder="parent@example.com"
                value={form.guardian_email}
                onChange={e => setForm(f => ({ ...f, guardian_email: e.target.value }))}
                data-testid="intake-new-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Guardian phone <span className="text-muted-foreground text-[11px]">(optional)</span></Label>
              <Input
                value={form.guardian_phone}
                onChange={e => setForm(f => ({ ...f, guardian_phone: e.target.value }))}
                data-testid="intake-new-phone"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Student name hint <span className="text-muted-foreground text-[11px]">(optional)</span></Label>
              <Input
                value={form.student_name_hint}
                onChange={e => setForm(f => ({ ...f, student_name_hint: e.target.value }))}
                data-testid="intake-new-name-hint"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Branch</Label>
              <Select value={form.branch_id} onValueChange={v => setForm(f => ({ ...f, branch_id: v }))}>
                <SelectTrigger data-testid="intake-new-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent className="bg-card">
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label>Internal note <span className="text-muted-foreground text-[11px]">(not shown to the family)</span></Label>
              <Textarea rows={2}
                value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={create} disabled={creating} data-testid="intake-new-create">
              {creating ? "Creating…" : "Create intake link"}
            </Button>
          </div>
        </div>

        {/* Existing intakes */}
        <div className="space-y-2">
          <div className="text-sm font-semibold flex items-center gap-2">
            Recent intakes
            <span className="text-xs font-normal text-muted-foreground">({intakes.length})</span>
          </div>
          {intakes.length === 0 ? (
            <div className="text-xs text-muted-foreground p-4 text-center border rounded-md">No intake links yet.</div>
          ) : (
            <div className="border rounded-md divide-y max-h-[300px] overflow-y-auto">
              {intakes.map(it => {
                const canResend = ["sent", "opened"].includes(it.status) && it.sent_to_email;
                const canCancel = !["submitted", "cancelled"].includes(it.status);
                return (
                  <div key={it.id} className="p-3 text-xs flex items-start gap-3" data-testid={`intake-row-${it.id}`}>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={`${STATUS_STYLES[it.status] || "bg-slate-100 text-slate-700"} text-[10px] uppercase`}>{it.status}</Badge>
                        {it.student_name_hint && <span className="font-medium text-sm">{it.student_name_hint}</span>}
                      </div>
                      {it.sent_to_email && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <EnvelopeSimple size={12} /> {it.sent_to_email}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Clock size={12} /> Created {fmtDate(it.created_at)}
                        {it.submitted_at && <> · Submitted {fmtDate(it.submitted_at)}</>}
                        {it.resent_at && <> · Resent {fmtDate(it.resent_at)}</>}
                      </div>
                      {it.note && <div className="text-muted-foreground italic">Note: {it.note}</div>}
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button
                        size="sm" variant="outline" className="h-7 gap-1 text-[11px]"
                        onClick={() => copyLink(it.share_url)}
                        data-testid={`intake-copy-${it.id}`}
                      >
                        <LinkSimple size={12} /> Copy
                      </Button>
                      {canResend && (
                        <Button
                          size="sm" variant="outline" className="h-7 gap-1 text-[11px]"
                          onClick={() => resend(it.id)} disabled={busyId === it.id}
                          data-testid={`intake-resend-${it.id}`}
                        >
                          <PaperPlaneTilt size={12} /> Resend
                        </Button>
                      )}
                      {canCancel && (
                        <Button
                          size="sm" variant="ghost" className="h-7 gap-1 text-[11px] text-red-600 hover:bg-red-50"
                          onClick={() => cancel(it.id)} disabled={busyId === it.id}
                          data-testid={`intake-cancel-${it.id}`}
                        >
                          <XCircle size={12} /> Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
